import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import DefaultLayout from "../../layouts/DefaultLayout";
import Loader from "../../components/Loader";
import Alert from "../../components/Alert";
import ConfirmationModal from "../../components/ConfirmationModal";
import {
  deleteDriver,
  getListDrivers,
} from "../../redux/actions/driverActions";

function DriverScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const page = searchParams.get("page") || "1";

  const [eventType, setEventType] = useState("");
  const [driverId, setDriverId] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loadEvent, setLoadEvent] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const listDrivers = useSelector((state) => state.getListDrivers);
  const { drivers, loadingListDrivers, errorListDrivers, pages } = listDrivers;

  const driverDelete = useSelector((state) => state.deleteDriver);
  const { successDriverDelete, loadingDriverDelete, errorDriverDelete } =
    driverDelete;

  const redirect = "/";

  useEffect(() => {
    if (!userInfo) {
      navigate(redirect);
    } else {
      dispatch(getListDrivers(page));
    }
  }, [navigate, userInfo, dispatch, page]);

  useEffect(() => {
    if (successDriverDelete) {
      dispatch(getListDrivers(1));
    }
  }, [successDriverDelete]);

  // Filter drivers based on search term and status
  const filteredDrivers = drivers?.filter(driver => {
    // Search term filter
    const matchesSearch = !searchTerm ||
      (driver.first_name && driver.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (driver.last_name && driver.last_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (driver.email && driver.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (driver.phone && driver.phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (driver.city && driver.city.toLowerCase().includes(searchTerm.toLowerCase()));

    // Always return true for "all" status filter
    return matchesSearch;
  });

  // Handle pagination
  const handlePagination = (newPage) => {
    if (newPage < 1 || newPage > pages) return;
    navigate(`/drivers?page=${newPage}`);
  };

  return (
    <DefaultLayout>
      <div className="animate-fadeIn">
        {/* Modern Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <a
                href="/dashboard"
                className="inline-flex items-center text-sm font-medium text-primary hover:text-secondary transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  ></path>
                </svg>
                Dashboard
              </a>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-secondary"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <span className="ml-1 text-sm font-medium text-secondary md:ml-2">
                  Drivers
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header with background */}
        <div
          className="relative mb-8 rounded-xl overflow-hidden bg-gradient-to-r from-primary to-secondary shadow-lg"
          style={{
            backgroundImage: "url('/assets/background-auth-screen.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-secondary/80"></div>
          <div className="relative z-10 px-6 py-8 md:px-10 md:py-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Drivers
                </h1>
                <p className="text-white/80 max-w-xl">
                  View and manage your delivery drivers and their information.
                </p>
              </div>
              <div className="mt-4 md:mt-0 flex items-center gap-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <div className="flex items-center">
                    <div className="mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-white">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-white/70 text-xs">Total Drivers</p>
                      <p className="text-white font-bold text-xl">{drivers?.length || 0}</p>
                    </div>
                  </div>
                </div>
                <Link
                  to="/drivers/new-driver"
                  className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium py-3 px-5 rounded-lg border border-white/20 backdrop-blur-sm transition-colors duration-200"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 4.5v15m7.5-7.5h-15"
                    />
                  </svg>
                  Add New Driver
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="w-full md:w-1/3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search drivers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border-2 border-stroke focus:border-primary focus:outline-none transition-colors duration-200 pl-10"
                />
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                  className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-bodydark">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Drivers Table */}
        <div className="rounded-xl border border-stroke bg-white shadow-default overflow-hidden">
          <div className="p-4 md:p-6">
            {loadingListDrivers ? (
              <Loader />
            ) : errorListDrivers ? (
              <Alert type={"error"} message={errorListDrivers} />
            ) : (
              <div className="w-full overflow-x-auto">
                <table className="w-full table-auto">
                  <thead>
                    <tr className="bg-gray-1 text-left">
                      <th className="py-4 px-4 font-semibold text-black text-sm">
                        ID
                      </th>
                      <th className="py-4 px-4 font-semibold text-black text-sm">
                        Name
                      </th>
                      <th className="py-4 px-4 font-semibold text-black text-sm">
                        Contact
                      </th>
                      <th className="py-4 px-4 font-semibold text-black text-sm">
                        Location
                      </th>
                      <th className="py-4 px-4 font-semibold text-black text-sm text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDrivers?.length > 0 ? (
                      filteredDrivers.map((driver, index) => (
                        <tr
                          key={driver.id}
                          className={`border-b border-stroke hover:bg-gray-1 transition-colors duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-1/30'}`}
                        >
                          <td className="py-4 px-4">
                            <span className="inline-flex items-center justify-center px-2.5 py-1 text-xs font-medium rounded-md bg-primary/10 text-primary">
                              #{driver.id}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center">
                              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary mr-3">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                                </svg>
                              </div>
                              <div>
                                <p className="font-medium text-black">
                                  {driver.first_name || ""} {driver.last_name || ""}
                                </p>
                                <p className="text-xs text-bodydark">
                                  Driver
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="space-y-1">
                              <div className="flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-bodydark mr-2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                                </svg>
                                <span className="text-sm">{driver.email || "---"}</span>
                              </div>
                              <div className="flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-bodydark mr-2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                                </svg>
                                <span className="text-sm">{driver.phone || "---"}</span>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-bodydark mr-2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                              </svg>
                              <span className="text-sm">{driver.city || "---"}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center justify-center space-x-2">
                              <Link
                                to={"/drivers/edit/" + driver.id}
                                className="p-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors duration-200"
                                title="Edit Driver"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                                </svg>
                              </Link>
                              <button
                                onClick={() => {
                                  setEventType("delete");
                                  setDriverId(driver.id);
                                  setSelectedDriver(driver);
                                  setIsOpen(true);
                                }}
                                className="p-2 rounded-lg bg-danger/10 text-danger hover:bg-danger/20 transition-colors duration-200"
                                title="Delete Driver"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="py-8 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12 text-bodydark mb-3">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0a1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
                            </svg>
                            <p className="text-bodydark font-medium">No drivers found</p>
                            {searchTerm && (
                              <p className="text-sm text-bodydark mt-1">
                                Try adjusting your search criteria
                              </p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loadingListDrivers && !errorListDrivers && pages > 1 && (
            <div className="flex items-center justify-between border-t border-stroke px-6 py-4">
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-bodydark">
                    Showing page <span className="font-medium">{page}</span> of{" "}
                    <span className="font-medium">{pages}</span> pages
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => handlePagination(parseInt(page) - 1)}
                      disabled={parseInt(page) <= 1}
                      className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium ${
                        parseInt(page) <= 1
                          ? "bg-gray-1 text-bodydark cursor-not-allowed"
                          : "bg-white text-black hover:bg-gray-1 transition-colors duration-200"
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                      </svg>
                    </button>

                    {/* Page numbers */}
                    {[...Array(pages).keys()].map((x) => (
                      <button
                        key={x + 1}
                        onClick={() => handlePagination(x + 1)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                          parseInt(page) === x + 1
                            ? "bg-primary text-white"
                            : "bg-white text-black hover:bg-gray-1 transition-colors duration-200"
                        }`}
                      >
                        {x + 1}
                      </button>
                    ))}

                    <button
                      onClick={() => handlePagination(parseInt(page) + 1)}
                      disabled={parseInt(page) >= pages}
                      className={`relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium ${
                        parseInt(page) >= pages
                          ? "bg-gray-1 text-bodydark cursor-not-allowed"
                          : "bg-white text-black hover:bg-gray-1 transition-colors duration-200"
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={isOpen}
          message={
            eventType === "delete"
              ? "Are you sure you want to delete this driver? This action cannot be undone."
              : "Are you sure ?"
          }
          onConfirm={async () => {
            if (eventType === "cancel") {
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
              setDriverId("");
            } else if (eventType === "delete" && driverId !== "") {
              setLoadEvent(true);
              dispatch(deleteDriver(driverId));
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
            } else {
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
              setDriverId("");
            }
          }}
          onCancel={() => {
            setIsOpen(false);
            setEventType("");
            setLoadEvent(false);
            setDriverId("");
          }}
          loadEvent={loadEvent}
        />
      </div>
    </DefaultLayout>
  );
}

export default DriverScreen;
